import React, { useRef, useState } from 'react';
import styled from 'styled-components';
import { ProductCopyStockDropdown } from './ProductCopyStock';
import { ProductMetaData } from '../../../models/Product';
import MetadataUtils from '../utils/utils-metadata';
import { THEME_PRIMARY, themeColor } from '../../styles/basics/colors';
import MaterialIcon from '../common/MaterialIcon';
import { PRODUCT_CLASS_CAMPAIGN } from '../../store/constants';
import { Placement } from '@popperjs/core';
import ProductAvailableDates from './ProductAvailableDates';
import { Trans, useTranslation } from 'react-i18next';
import PreOrderStockDetailsList from './PreOrderStockDetailsList';
import Utils from '../utils';
import { LOANFORMAT_EPRESS } from '../loanFormat/constants';
import { concreteLoanFormatKeyToObject } from '../loanFormat/mappers';
import { useAuthentication } from '../../app-store/authentication/useAuthentication';

const StMaterialIcon = styled(MaterialIcon)`
    font-size: 12px;
    line-height: unset;
    vertical-align: top;
`;

const StCopiesInCart = styled.li.attrs({ title: 'Copies in cart' })<{ readonly showLabels?: boolean }>`
    color: ${themeColor(THEME_PRIMARY)};
    float: ${({ showLabels }) => (showLabels ? 'inherit' : 'right !important')};
    margin-right: ${({ showLabels }) => (showLabels ? 'inherit' : '0 !important')};
`;
const StCopiesLabel = styled.div<{ readonly isLibraryManagedProduct?: boolean }>`
    color: ${({ isLibraryManagedProduct }) => (isLibraryManagedProduct ? 'red' : 'inherit')};
`;

type Props = {
    readonly product: ProductMetaData;
    readonly loanFormatKey: string;
    readonly showLabels?: boolean;
    readonly additionalClassNames?: string;
    readonly isLibraryManagedProduct?: boolean;
    readonly placement?: Placement;
};

export function showReserveWarning(reserveCount: number, copyStockAmount: number) {
    return reserveCount / copyStockAmount > 2;
}

function ProductStatistics({
    product,
    loanFormatKey,
    showLabels = false,
    additionalClassNames = '',
    placement = 'top-start'
}: Props) {
    const { t } = useTranslation();
    const [showReserveInfoSplit, setShowReserveInfoSplit] = useState(false);
    const [showCopyStockSplit, setShowCopyStockSplit] = useState(false);
    const isInPartnerContext = Utils.isInPartnerContext(useAuthentication());
    const copyStockSplitPopoverRef = useRef<HTMLDivElement>(null);

    const loanFormat = concreteLoanFormatKeyToObject(loanFormatKey);

    const { copiesInCart } = product;
    const copiesInCartComponent =
        (copiesInCart ?? 0) > 0 ? (
            <StCopiesInCart showLabels={showLabels}>
                <StMaterialIcon icon="shopping_cart" />{' '}
                {showLabels ? t('components.productStatistics.copiesInCart', { copiesInCart }) : copiesInCart}
            </StCopiesInCart>
        ) : null;

    const isCampaignProduct = product.productClass === PRODUCT_CLASS_CAMPAIGN;

    const { preOrderStockDetails, stockDetails, copyStockAmount = 0, combinedReserveInfo } = product;

    if (MetadataUtils.isProductPreOrderable(product)) {
        return (
            <ul className={`prd-dtl ${additionalClassNames}`}>
                {
                    <PreOrderStockDetailsList
                        preOrderStockDetails={preOrderStockDetails}
                        loanFormat={loanFormat!}
                        showLabels={showLabels}
                        isCampaignProduct={isCampaignProduct}
                    />
                }
                {copiesInCartComponent}
            </ul>
        );
    }

    const foreignReserveCount = isInPartnerContext ? 0 : combinedReserveInfo?.foreignReserveCount ?? 0;
    const ownReserveCount = isInPartnerContext
        ? combinedReserveInfo?.foreignReserveCount ?? 0
        : combinedReserveInfo?.ownReserveCount ?? 0;

    const reserveAmount = foreignReserveCount > 0 ? `${ownReserveCount}+${foreignReserveCount}` : `${ownReserveCount}`;
    const reserveWarning = showReserveWarning(
        (combinedReserveInfo?.ownReserveCount ?? 0) + (combinedReserveInfo?.foreignReserveCount ?? 0),
        copyStockAmount
    );

    return (
        <ul className={`prd-dtl ${additionalClassNames}`}>
            <li>
                {(copyStockAmount ?? 0) <= 0 || !stockDetails ? (
                    <StCopiesLabel title={t('components.productStatistics.copiesInYourLibrary')}>
                        <StMaterialIcon icon="content_copy" />{' '}
                        {showLabels ? (
                            <Trans
                                i18nKey="components.productStatistics.inYourLibraryWithLabels"
                                components={{ span: <span /> }}
                                values={{ copyStockAmount: 0 }}
                            />
                        ) : (
                            <span>0</span>
                        )}
                    </StCopiesLabel>
                ) : (
                    <div
                        style={{ display: 'inline-block' }}
                        onMouseEnter={() => setShowCopyStockSplit(true)}
                        onMouseLeave={() => setShowCopyStockSplit(false)}
                    >
                        <span ref={copyStockSplitPopoverRef}>
                            <StMaterialIcon icon="content_copy" />
                        </span>{' '}
                        {showLabels ? (
                            <Trans
                                i18nKey="components.productStatistics.inYourLibraryWithLabels"
                                components={{ span: <span /> }}
                                values={{ copyStockAmount }}
                            />
                        ) : (
                            <span>{copyStockAmount}</span>
                        )}
                        <ProductCopyStockDropdown
                            loanFormat={loanFormat!}
                            stockDetails={stockDetails}
                            placement={placement}
                            visible={showCopyStockSplit}
                            popoverRef={copyStockSplitPopoverRef}
                        />
                    </div>
                )}
            </li>
            {preOrderStockDetails ? (
                <PreOrderStockDetailsList
                    preOrderStockDetails={preOrderStockDetails}
                    loanFormat={loanFormat!}
                    showLabels={showLabels}
                    isCampaignProduct={isCampaignProduct}
                />
            ) : null}
            {copyStockAmount > 0 && loanFormat?.key !== LOANFORMAT_EPRESS ? (
                // In this case the site or the partner network doesn't have any copy of the product.
                // => we don't need to calculate the reserve statistics
                <li
                    className={reserveWarning ? 'cold-red' : ''}
                    title={t('components.productStatistics.numberOfReserves')}
                    onMouseLeave={() => setShowReserveInfoSplit(false)}
                    onMouseEnter={() => setShowReserveInfoSplit(true)}
                >
                    <StMaterialIcon icon="person" />{' '}
                    {showLabels ? t('components.productStatistics.reservesQueue', { reserveAmount }) : reserveAmount}
                    {showReserveInfoSplit ? (
                        <div className="default-dd details-dropdown reserve-amount-details">
                            <span className="active-arrow" />
                            <div className="reserve-dropdown-header">
                                <strong>{t('components.productStatistics.currentreservesQueue')}</strong>
                            </div>
                            <div className="reserve-amount-info">
                                <Trans
                                    i18nKey="components.productStatistics.yourUsers"
                                    components={{ strong: <strong /> }}
                                    values={{ ownReserveCount }}
                                />
                            </div>
                            {foreignReserveCount > 0 ? (
                                <div className="reserve-amount-info">
                                    <Trans
                                        i18nKey="components.productStatistics.usersOnOtherSites"
                                        components={{ strong: <strong /> }}
                                        values={{ foreignReserveCount }}
                                    />
                                </div>
                            ) : null}
                            <div className="available-dates">
                                <div className="availability-info">
                                    <strong>{t('components.productStatistics.availableOnDate')}</strong>
                                </div>
                                <ProductAvailableDates productId={product.id} />
                            </div>
                        </div>
                    ) : null}
                </li>
            ) : null}
            {copiesInCartComponent}
        </ul>
    );
}

export default ProductStatistics;
